import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NameFields from "@/components/NameFields";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";

const SPECIALTY_SUGGESTIONS = [
    "Shooting", "Ball Handling", "Skills", "Post Play", "Defense",
    "Strength & Conditioning", "Speed", "Footwork", "Yoga", "Recovery", "Mental Game",
];

export default function RegisterTrainer() {
    const navigate = useNavigate();
    const { registerTrainer } = useAuth();
    const [form, setForm] = useState({
        first_name: "", middle_initial: "", last_name: "", email: "", password: "",
        home_zip: "", years_experience: 5, hourly_rate: 75,
        bio: "", photo: "", detail_photo: "",
    });
    const [specialties, setSpecialties] = useState([]);
    const [customSpec, setCustomSpec] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const setN = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) });

    const toggleSpec = (s) => {
        setSpecialties((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
    };
    const addCustom = () => {
        const v = customSpec.trim();
        if (v && !specialties.includes(v)) setSpecialties([...specialties, v]);
        setCustomSpec("");
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.first_name.trim() || !form.last_name.trim()) { setError("First and last name are required"); return; }
        const mi = form.middle_initial ? `${form.middle_initial}. ` : "";
        const name = `${form.first_name.trim()} ${mi}${form.last_name.trim()}`.trim();
        setSubmitting(true);
        const { first_name, middle_initial, last_name, ...rest } = form;
        const res = await registerTrainer({
            ...rest,
            name,
            specialties,
            years_experience: Number(form.years_experience),
            hourly_rate: Number(form.hourly_rate),
        });
        setSubmitting(false);
        if (res.ok) navigate("/");
        else setError(res.error);
    };

    return (
        <div className="max-w-2xl mx-auto px-6 py-12">
            <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-2">Trainer signup</div>
            <h1 className="font-display text-5xl tracking-wide">Publish your services</h1>
            <p className="text-sm text-[#a0a0a0] mt-2">
                Trainers get a public profile so players, parents, and teams can find you by ZIP and specialty.{" "}
                <Link to="/register" className="text-[#ff4500] hover:underline">Wrong type?</Link>
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-trainer-form">
                <NameFields testidPrefix="trainer" section="trainer" first={form.first_name} mi={form.middle_initial} last={form.last_name} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
                <F label="Email" type="email" testid="trainer-email" value={form.email} onChange={set("email")} required autoComplete="email" />
                <p className="text-[10px] text-[#ff4500] -mt-2">Your email can't be changed later.</p>
                <F label="Password (min 6 chars)" type="password" testid="trainer-password" value={form.password} onChange={set("password")} required minLength={6} />

                <div className="grid grid-cols-2 gap-4">
                    <F label="Home ZIP (5 digits)" testid="trainer-zip" value={form.home_zip} onChange={set("home_zip")} required pattern="[0-9]{5}" />
                    <F label="Years Experience" type="number" testid="trainer-years" value={form.years_experience} onChange={setN("years_experience")} required min={0} max={60} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <F label="Hourly Rate (USD)" type="number" testid="trainer-rate" value={form.hourly_rate} onChange={setN("hourly_rate")} min={0} max={1000} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PhotoUpload value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} aspect={1} square label="Headshot (square)" hint="Square crop · shown on cards & listings." dragPaste testid="trainer-photo" />
                    <PhotoUpload value={form.detail_photo} onChange={(v) => setForm((f) => ({ ...f, detail_photo: v }))} aspect={null} label="Trainer photo (detail page)" hint="Free-form crop · full resolution on your profile." dragPaste testid="trainer-detail-photo" />
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Specialties</Label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="trainer-specialty-chips">
                        {SPECIALTY_SUGGESTIONS.map((s) => (
                            <button
                                type="button"
                                key={s}
                                data-testid={`spec-${s}`}
                                onClick={() => toggleSpec(s)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                                    specialties.includes(s)
                                        ? "bg-[#ff4500] border-[#ff4500] text-white"
                                        : "bg-[#141414] border-[#2a2a2a] text-[#a0a0a0] hover:border-[#ff4500]"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2 mt-3">
                        <Input
                            data-testid="trainer-spec-custom"
                            value={customSpec}
                            onChange={(e) => setCustomSpec(e.target.value)}
                            placeholder="Add custom specialty…"
                            className="h-10 rounded-none bg-[#141414] border-[#2a2a2a]"
                        />
                        <Button type="button" onClick={addCustom} className="rounded-none bg-[#2a2a2a] hover:bg-[#ff4500] uppercase tracking-wider text-xs">
                            Add
                        </Button>
                    </div>
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Bio (optional)</Label>
                    <Textarea
                        data-testid="trainer-bio"
                        value={form.bio}
                        onChange={set("bio")}
                        rows={3}
                        placeholder="Tell athletes about your approach, credentials, achievements…"
                        className="mt-2 rounded-none bg-[#141414] border-[#2a2a2a]"
                    />
                </div>

                {error && <div data-testid="trainer-error" className="text-sm text-[#ff4500] border border-[#ff4500] p-3">{error}</div>}

                <Button type="submit" disabled={submitting} data-testid="trainer-submit" className="w-full h-12 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-xs font-semibold">
                    {submitting ? "Creating…" : "Create Trainer Profile"}
                </Button>
            </form>
        </div>
    );
}

function F({ label, testid, ...props }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</Label>
            <Input data-testid={testid} {...props} className="mt-2 h-12 rounded-none bg-[#141414] border-[#2a2a2a]" />
        </div>
    );
}
